import {
  CatalogCategory,
  catalogCategoriesOf,
  CatalogPiece,
  countByCatalogCategory,
  orderCatalogCategories,
} from './catalog-categories';

export type CatalogCategoryCount = {
  category: CatalogCategory;
  count: number;
};

export type CatalogView<T extends CatalogPiece> = {
  pieces: T[];
  matchingCount: number;
  categoryCounts: CatalogCategoryCount[];
};

type BuildCatalogViewParams<T extends CatalogPiece> = {
  pieces: T[];
  search: string;
  category: CatalogCategory | null;
  locale: string;
};

const DIACRITICS = /\p{Diacritic}/gu;

const normalizeForSearch = (text: string): string =>
  text.normalize('NFD').replace(DIACRITICS, '').toLowerCase().trim();

export const sortByDisplayName = <T extends { displayName: string }>(
  items: T[],
  locale: string,
): T[] =>
  [...items].sort((a, b) => a.displayName.localeCompare(b.displayName, locale));

export const buildCatalogView = <T extends CatalogPiece>({
  pieces,
  search,
  category,
  locale,
}: BuildCatalogViewParams<T>): CatalogView<T> => {
  const needle = normalizeForSearch(search);
  const matchingPieces = pieces.filter((piece) =>
    normalizeForSearch(`${piece.displayName} ${piece.description}`).includes(
      needle,
    ),
  );
  const matchingCounts = countByCatalogCategory(matchingPieces);
  const categoryCounts = orderCatalogCategories(pieces)
    .map((listedCategory) => ({
      category: listedCategory,
      count: matchingCounts.get(listedCategory) ?? 0,
    }))
    .filter((entry) => entry.count > 0 || entry.category === category);
  const visiblePieces =
    category === null
      ? matchingPieces
      : matchingPieces.filter((piece) =>
          catalogCategoriesOf(piece).includes(category),
        );

  return {
    pieces: sortByDisplayName(visiblePieces, locale),
    matchingCount: matchingPieces.length,
    categoryCounts,
  };
};
